import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { activateUpdateAndReload, checkForServiceWorkerUpdate } from '../pwa';

export type AppEnv = 'production' | 'staging' | 'development';

interface VersionContextType {
  showVersionMismatchPopup: boolean;
  dismissPopup: () => void;
  hardRefresh: () => void;
  connectionStatus: 'online' | 'offline' | 'outdated' | 'unauthorized';
  currentVersion: string;
  latestVersion: string | null;
  // Mediul raportat de backend prin /version. null până la primul răspuns reușit.
  appEnv: AppEnv | null;
  // Istoricul de versiuni (ChangelogModal). Starea stă aici pentru că modalul e deschis din două
  // locuri: bulina de status din bara de sus și popup-ul de versiune nouă.
  changelogOpen: boolean;
  openChangelog: () => void;
  closeChangelog: () => void;
}

const VersionContext = createContext<VersionContextType | null>(null);

export const useVersion = () => {
  const context = useContext(VersionContext);
  if (!context) {
    throw new Error('useVersion must be used within a VersionProvider');
  }
  return context;
};

interface VersionProviderProps {
  children: React.ReactNode;
}

export const VersionProvider: React.FC<VersionProviderProps> = ({ children }) => {
  const [showVersionMismatchPopup, setShowVersionMismatchPopup] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'online' | 'offline' | 'outdated' | 'unauthorized'>('online');
  const [currentVersion] = useState('2.4.30'); // versiune
  const [latestVersion, setLatestVersion] = useState<string | null>(null);
  const [appEnv, setAppEnv] = useState<AppEnv | null>(null);
  const [changelogOpen, setChangelogOpen] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const dismissTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const CHECK_INTERVAL = 10000; // 10 seconds
  const DISMISS_DURATION = 200000; // 2 minutes  

  const checkVersion = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.VITE_BACKEND_URI}/version`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : ''
        }
      });
      const backendVersion = response.data.version;
      setLatestVersion(backendVersion);
      if (response.data.appEnv) {
        setAppEnv(response.data.appEnv as AppEnv);
      }

      if (currentVersion !== backendVersion) {
        setConnectionStatus('outdated');
        // Pornim din timp descărcarea service worker-ului nou, ca apăsarea butonului
        // din popup să găsească versiunea deja în așteptare și să reîncarce imediat.
        void checkForServiceWorkerUpdate();
        if (!isDismissed) {
          setShowVersionMismatchPopup(true);
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        }
      } else {
        setConnectionStatus('online');
      }
    } catch (error: any) {
      console.warn('Failed to check version:', error);

      if (error.response?.status === 401) {
        setConnectionStatus('unauthorized');
      } else {
        setConnectionStatus('offline');
      }
      setLatestVersion(null);
    }
  };

  const dismissPopup = () => {
    setShowVersionMismatchPopup(false);
    setIsDismissed(true);

    if (dismissTimeoutRef.current) {
      clearTimeout(dismissTimeoutRef.current);
    }

    dismissTimeoutRef.current = setTimeout(() => {
      setIsDismissed(false);
      if (!intervalRef.current) {
        intervalRef.current = setInterval(checkVersion, CHECK_INTERVAL);
      }
    }, DISMISS_DURATION);
  };

  const hardRefresh = () => {
    // Un `location.replace` simplu NU ocolește service worker-ul: ar servi din nou
    // shell-ul vechi din cache, iar popup-ul ar reapărea la infinit. Preluarea
    // versiunii noi se face explicit, iar reîncărcarea vine după.
    void activateUpdateAndReload();
  };

  const openChangelog = () => setChangelogOpen(true);
  const closeChangelog = () => setChangelogOpen(false);

  useEffect(() => {
    checkVersion();
    if (!intervalRef.current) {
      intervalRef.current = setInterval(checkVersion, CHECK_INTERVAL);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (dismissTimeoutRef.current) {
        clearTimeout(dismissTimeoutRef.current);
      }
    };
  }, []);

  return (
    <VersionContext.Provider value={{
      showVersionMismatchPopup,
      dismissPopup,
      hardRefresh,
      connectionStatus,
      currentVersion,
      latestVersion,
      appEnv,
      changelogOpen,
      openChangelog,
      closeChangelog
    }}>
      {children}
    </VersionContext.Provider>
  );
};